import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';

export type Notice = Readonly<{
  title: string;
  body?: string;
}>;

type NoticeContextValue = Readonly<{
  notice: Notice | null;
  showNotice: (notice: Notice) => void;
  clearNotice: () => void;
}>;

const NoticeContext = createContext<NoticeContextValue | null>(null);

export const NoticeProvider = ({ children }: PropsWithChildren) => {
  const [notice, setNotice] = useState<Notice | null>(null);
  const showNotice = useCallback((next: Notice) => setNotice(next), []);
  const clearNotice = useCallback(() => setNotice(null), []);
  const value = useMemo(() => ({ notice, showNotice, clearNotice }), [notice, showNotice, clearNotice]);

  return <NoticeContext.Provider value={value}>{children}</NoticeContext.Provider>;
};

export const useNotice = (): NoticeContextValue => {
  const context = useContext(NoticeContext);
  if (!context) {
    throw new Error('useNotice must be used inside NoticeProvider');
  }
  return context;
};
